import { motion } from 'framer-motion';
import { Building2, Globe, Users, Rocket, Handshake, Star, ChevronRight } from 'lucide-react';

const PartnersPage = () => {
  const partners = [
    { name: 'Tech Innovators', logo: '/logos/tech-innovators.svg', url: '#' },
    { name: 'Digital Solutions', logo: '/logos/digital-solutions.svg', url: '#' },
    { name: 'Future Labs', logo: '/logos/future-labs.svg', url: '#' },
    { name: 'Cloud Masters', logo: '/logos/cloud-masters.svg', url: '#' },
    { name: 'Data Wizards', logo: '/logos/data-wizards.svg', url: '#' },
    { name: 'AI Pioneers', logo: '/logos/ai-pioneers.svg', url: '#' },
  ];

  const stats = [
    { icon: <Globe className="w-6 h-6" />, value: '85+', label: 'Global Reach' },
    { icon: <Users className="w-6 h-6" />, value: '1.2K+', label: 'Strategic Alliances' },
    { icon: <Rocket className="w-6 h-6" />, value: '15K+', label: 'Collaborative Projects' },
    { icon: <Star className="w-6 h-6" />, value: '98%', label: 'Success Ratio' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-end pb-24 px-4 bg-[#1e3a8a] overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-[#1c3d6e] transform -skew-y-6 origin-top-right"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ type: 'spring', delay: 0.2, stiffness: 50 }}
        />
        
        <motion.div 
          className="max-w-7xl mx-auto relative z-10 text-white"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end">
            <motion.div className="space-y-8" variants={itemVariants}>
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <Handshake className="h-16 w-16 text-[#334E7D]" />
              </motion.div>
              <motion.h1 
                className="text-5xl md:text-6xl font-bold leading-tight"
                variants={itemVariants}
              >
                Redefining Corporate <br/>
                <span className="text-[#334E7D]">Collaboration</span>
              </motion.h1>
            </motion.div>

            <motion.div 
              className="bg-white/10 p-8 rounded-2xl backdrop-blur-lg"
              variants={itemVariants}
            >
              <p className="text-xl mb-6">Global enterprise network driving innovation at scale</p>
              <motion.button 
                className="flex items-center gap-2 bg-[#334E7D] hover:bg-[#1c3d6e] px-8 py-4 rounded-xl font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                Explore Synergies
                <ChevronRight className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </div>
        </motion.div>

        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 20, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ChevronRight className="w-8 h-8 text-[#334E7D] transform rotate-90" />
        </motion.div>
      </section>

      {/* Partners Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 transform -translate-y-32"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {partners.map((partner, index) => (
              <motion.div 
                key={index} 
                className={`bg-white p-6 rounded-2xl shadow-2xl ${index % 3 === 0 ? 'h-48' : 'h-64'}`}
                variants={itemVariants}
                whileHover={{ y: -10 }}
              >
                <img 
                  src={partner.logo} 
                  alt={partner.name} 
                  className="w-full h-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-24 bg-[#334E7D]">
        <motion.div 
          className="absolute inset-0 bg-[#1e3a8a] transform -skew-y-3 origin-top"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ type: 'spring', delay: 0.1 }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            variants={containerVariants}
          >
            {stats.map((stat, index) => (
              <motion.div 
                key={index} 
                className="text-center p-8 bg-white/5 backdrop-blur-lg rounded-xl border border-white/10"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <div className="text-white mb-4 mx-auto">{stat.icon}</div>
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-300 uppercase text-sm tracking-wide">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <motion.div 
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          variants={containerVariants}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <motion.div 
              className="lg:col-span-2 bg-[#1c3d6e] p-12 rounded-3xl text-white"
              variants={itemVariants}
            >
              <h2 className="text-4xl font-bold mb-6">Strategic Enterprise Network</h2>
              <p className="text-lg text-gray-300 mb-8">
                Leverage our global ecosystem of industry leaders, innovators, and change-makers 
                to accelerate your corporate objectives through strategic alliances.
              </p>
              <div className="flex gap-4">
                <motion.button 
                  className="bg-[#334E7D] hover:bg-[#1e3a8a] px-8 py-3 rounded-lg font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Partnership Models
                </motion.button>
                <motion.button 
                  className="border-2 border-white/20 hover:border-white/40 px-8 py-3 rounded-lg font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Case Studies
                </motion.button>
              </div>
            </motion.div>

            <motion.div 
              className="bg-[#1e3a8a] p-12 rounded-3xl text-white flex flex-col justify-between"
              variants={itemVariants}
            >
              <div>
                <motion.div
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <Star className="w-12 h-12 mb-6 text-[#334E7D]" />
                </motion.div>
                <h3 className="text-2xl font-bold mb-4">Premium Alliance Benefits</h3>
                <ul className="space-y-4 text-gray-300">
                  {['Exclusive Market Insights', 'Priority Co-Innovation', 'Global Leadership Forums'].map((item, i) => (
                    <motion.li 
                      key={i} 
                      className="flex items-center gap-3"
                      whileHover={{ x: 10 }}
                    >
                      <ChevronRight className="w-5 h-5 text-[#334E7D]" />
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </div>
              <motion.button 
                className="mt-8 w-full bg-[#334E7D] hover:bg-[#1c3d6e] py-4 rounded-xl font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Elevate Partnership
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="relative h-96 bg-[#1e3a8a] overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-grid-white/10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5 }}
        />
        <div className="relative h-full flex items-center justify-center">
          <motion.div 
            className="text-center max-w-2xl px-4"
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 50 }}
          >
            <motion.div
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <Building2 className="h-16 w-16 mx-auto mb-6 text-[#334E7D]" />
            </motion.div>
            <h2 className="text-4xl font-bold text-white mb-6">
              Architect the Future of Corporate Collaboration
            </h2>
            <motion.button 
              className="bg-white text-[#1e3a8a] px-8 py-3 rounded-lg font-medium hover:bg-gray-100"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Initiate Partnership
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;